import ProgressBar from '@jetbrains/ring-ui/components/progress-bar/progress-bar'
import * as React from 'react'

import {useFetchEffect} from '../../../../../hocs/withEffect'
import {useIsAgentsPartSelected} from '../../../../../hooks/routes'
import {PlaceIdList} from '../../../../../pluginSystem/pluginSystem.types'
import {getIsSakuraUI} from '../../../../../selectors'
import {subscribeonStatisticsUpdates} from '../../../../../utils/subscriber'
import Plugin from '../../../../common/Plugin/Plugin'
import {getCachedPlugins} from '../../../../common/Plugin/Plugin.selectors'
import {headerAgentsLinkHint} from '../../../../packages/Hints/HintItems/Hints.navigation'
import {useHint} from '../../../../packages/Hints/Hints.hooks'
import {LINKS} from '../../Header.consts'
import HeaderLinkHost from '../LinkHost/LinkHost'
import type {StatisticsUpdateData} from '../Links.types'
import {isActiveClassicUILink} from '../Links.utils'

import {calculateContent} from './AgentsLink.utils'

import styles from './AgentsLink.css'

import {useAppSelector} from 'src/hooks/react-redux'

function AgentLink() {
  useHint(headerAgentsLinkHint)
  const isSakuraUI = useAppSelector(getIsSakuraUI)
  const [statistics, setStatistics] = React.useState<StatisticsUpdateData | null | undefined>(null)
  const {agentsNum = 0, freeAgentsNum, licensedConcurrentBuilds = 0} = statistics ?? {}

  const customAgentsCounter = useAppSelector(
    state => getCachedPlugins(state, PlaceIdList.SAKURA_CUSTOM_AGENTS_COUNTER).length > 0,
  )
  const agentsPartSelected = useIsAgentsPartSelected()
  const active = isSakuraUI ? agentsPartSelected : isActiveClassicUILink('agents')
  useFetchEffect(() => {
    if (customAgentsCounter) {
      return undefined
    }

    return subscribeonStatisticsUpdates((data: StatisticsUpdateData | null | undefined) =>
      setStatistics(data),
    )
  }, [customAgentsCounter])
  const {agentsLoad, title} = React.useMemo(
    () => calculateContent(agentsNum, freeAgentsNum, licensedConcurrentBuilds),
    [agentsNum, freeAgentsNum, licensedConcurrentBuilds],
  )
  const postIcon = React.useMemo(() => {
    if (customAgentsCounter) {
      return <Plugin placeId="SAKURA_CUSTOM_AGENTS_COUNTER" withoutLoader />
    }

    const shouldRenderProgress = !isNaN(agentsLoad) && agentsNum > 0
    return (
      shouldRenderProgress && (
        <ProgressBar staticColor value={agentsLoad || 0} className={styles.progressBar} />
      )
    )
  }, [agentsNum, agentsLoad, customAgentsCounter])

  return React.useMemo(
    () => (
      <HeaderLinkHost
        active={active}
        to={LINKS.agents}
        title={customAgentsCounter ? 'Agents' : title}
        icon="agents-20px"
        postIcon={postIcon}
        counter={customAgentsCounter ? undefined : agentsNum}
        counterAttributes={{'data-hint-container-id': headerAgentsLinkHint.id}}
      >
        {'Agents'}
      </HeaderLinkHost>
    ),
    [title, active, postIcon, customAgentsCounter, agentsNum],
  )
}

export default React.memo(AgentLink)
